import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { PublicNav } from "@/components/PublicNav";
import { AdSlot } from "@/components/AdSlot";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LABELS } from "@/lib/monteurOptions";
import { PROVINCIE_LIJST } from "@/lib/jobOptions";
import { PortfolioGallery } from "@/components/PortfolioGallery";
import { Users, MapPin, Star, Truck, Languages, Wrench, UserCheck, Lock, ArrowRight, BadgeCheck } from "lucide-react";

const SPEC_LABEL = { ramen: "Ramen", deuren: "Deuren", beide: "Ramen & Deuren" };
const DASH_BY_ROLE = { monteur: "/monteur", bedrijf: "/opdrachtgever", particulier: "/opdrachtgever", admin: "/admin" };

function PublicMonteurCard({ m, onOpen, isAuthed }) {
  return (
    <div data-testid={`pub-monteur-card-${m.id}`}
      className="group bg-white border border-slate-200 rounded-md p-5 flex flex-col hover:border-emerald-400 hover:shadow-lg transition-all">
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="rounded-sm w-12 h-12 bg-emerald-950">
          <AvatarFallback className="rounded-sm bg-emerald-950 text-emerald-400 font-bold">
            {m.name?.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-slate-950 flex items-center gap-1.5">
            {m.name}
            {m.pro && <BadgeCheck className="w-4 h-4 text-emerald-600" title="Pro-professional" />}
          </h3>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3 text-emerald-500" />
            {m.provincie || m.regio || "Provincie onbekend"}
          </p>
        </div>
        {m.beschikbaar && <span className="ml-auto w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" title="Beschikbaar" />}
      </div>
      {m.bio && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{m.bio}</p>}
      <div className="flex flex-wrap gap-2 text-xs mb-4">
        <Badge variant="secondary" className="rounded-sm">{SPEC_LABEL[m.specialisatie] || m.specialisatie}</Badge>
        {m.ervaring_jaren ? <Badge variant="outline" className="rounded-sm gap-1"><Star className="w-3 h-3" /> {m.ervaring_jaren} jr</Badge> : null}
        {m.pro && <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Pro</Badge>}
      </div>
      <div className="space-y-1.5 text-xs text-slate-600 border-t border-slate-100 pt-3 mb-4 flex-1">
        {m.werkstatuut && (
          <p className="flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5 text-slate-400" /> {LABELS[m.werkstatuut] || m.werkstatuut}</p>
        )}
        {m.vervoer && (
          <p className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-slate-400" /> {LABELS[m.vervoer] || m.vervoer}
            {m.rijbewijs_categorieen?.length ? ` · Rijbewijs ${m.rijbewijs_categorieen.join(", ")}` : ""}</p>
        )}
        {(m.spec_montage?.length || m.spec_afwerking?.length) ? (
          <p className="flex items-start gap-1.5"><Wrench className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
            <span>{[...(m.spec_montage || []), ...(m.spec_afwerking || [])].map((s) => LABELS[s] || s).join(", ")}</span></p>
        ) : null}
        {m.talen?.length ? (
          <p className="flex items-center gap-1.5"><Languages className="w-3.5 h-3.5 text-slate-400" /> {m.talen.join(", ")}
            {m.moedertaal ? ` · ${m.moedertaal} (moedertaal)` : ""}</p>
        ) : null}
        {m.leiding_team && (
          <p className="flex items-center gap-1.5 font-semibold text-slate-700"><Star className="w-3.5 h-3.5 text-emerald-500" /> Kan een team leiden</p>
        )}
      </div>
      <PortfolioGallery photos={m.portfolio} accent="emerald" label="Werk van deze professional" />
      <Button data-testid={`pub-monteur-open-${m.id}`} onClick={onOpen}
        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold w-full mt-4">
        {isAuthed ? (<>Bekijk & neem contact op <ArrowRight className="w-4 h-4 ml-2" /></>) : (<><Lock className="w-4 h-4 mr-2" /> Inloggen voor contact</>)}
      </Button>
    </div>
  );
}

export default function PublicMonteurs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: "Beschikbare professionals — Vakmensen voor ramen & deuren | Work4you",
    description: "Bekijk beschikbare professionals voor ramen- en deurenmontage in België en Nederland. Filter op provincie en specialisatie en neem na aanmelding direct contact op.",
    path: "/beschikbare_monteurs",
  });
  const [monteurs, setMonteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provincie, setProvincie] = useState("alle");
  const [specialisatie, setSpecialisatie] = useState("alle");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (provincie !== "alle") params.provincie = provincie;
    if (specialisatie !== "alle") params.specialisatie = specialisatie;
    api.get("/monteurs/public", { params })
      .then((r) => setMonteurs(r.data.monteurs || []))
      .catch(() => setMonteurs([]))
      .finally(() => setLoading(false));
  }, [provincie, specialisatie]);

  const handleOpen = () => {
    if (user && user.role) navigate(DASH_BY_ROLE[user.role] || "/");
    else navigate("/inloggen?next=beschikbare_monteurs");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-monteurs-page">
      <PublicNav active="monteurs" />

      {/* Groene banner */}
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <p className="label-caps text-emerald-400 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Professionalsgids
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] mb-4">
            Beschikbare <span className="text-emerald-400">professionals</span>
          </h1>
          <p className="text-lg text-emerald-100/80 max-w-2xl">
            Vind ervaren vakmensen voor ramen- en deurenmontage bij jou in de buurt. Meld je gratis aan als bedrijf of particulier om contact op te nemen.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <AdSlot slot="dashboard_top" publiek />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <Select value={provincie} onValueChange={setProvincie}>
            <SelectTrigger data-testid="pub-mont-filter-provincie" className="w-full sm:w-64 rounded-sm bg-white">
              <SelectValue placeholder="Provincie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle provincies</SelectItem>
              {PROVINCIE_LIJST.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={specialisatie} onValueChange={setSpecialisatie}>
            <SelectTrigger data-testid="pub-mont-filter-spec" className="w-full sm:w-64 rounded-sm bg-white">
              <SelectValue placeholder="Specialisatie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle specialisaties</SelectItem>
              <SelectItem value="ramen">Ramen</SelectItem>
              <SelectItem value="deuren">Deuren</SelectItem>
              <SelectItem value="beide">Ramen & Deuren</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-slate-500" data-testid="pub-monteurs-loading">Professionals laden…</p>
        ) : monteurs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-sm border border-slate-200" data-testid="pub-monteurs-empty">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">Geen professionals gevonden met deze filters.</p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="pub-monteurs-grid">
            {monteurs.map((m) => (
              <PublicMonteurCard key={m.id} m={m} isAuthed={!!(user && user.role)} onOpen={handleOpen} />
            ))}
          </div>
        )}

        {!user && (
          <div className="mt-12 bg-emerald-950 text-white rounded-sm p-8 text-center" data-testid="pub-monteurs-cta">
            <p className="font-display text-2xl font-extrabold mb-2">Op zoek naar een professional?</p>
            <p className="text-emerald-100/80 mb-5">Meld je gratis aan als bedrijf of particulier en neem direct contact op met vakmensen.</p>
            <Link to="/registreren">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm font-bold px-8 h-12">
                Gratis aanmelden <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
